import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { GlobalFilterContext } from './context';

/**
 * Filter object defining some properties
 * @typedef {object} filterObject
 * @property {string} [filterType] - The filter type
 * @property {string} [filter] - The value of the filter
 */

/**
 * @param {object} waterfallFilterStorage - The user preference service. It should
 * contain 2 methods:
 *  1. getFilters: get all filter values from user preference, it must be a synchronized function;
 *  2. save: save the filter object into server side, it should be an async function.
 * If it is null, the GlobalFilterContextProvider will not fetch/save the filters in the server side
 * @param {string[]} supportedWaterfallFilterTypes - The supported waterfall filter types. The
 * correspondingfilter would be fetched and saved in server side (user preferences).
 * @param {string[]} supportedUrlFilterTypes - The supported url filter types. The corresponding
 * filter would be cached in client side.
 * @param {any} filterDefaultValue - The default value for the filters if undefined
 * @param {node} children - React node, be wrapped in the GlobalFilterContextProvider
 * @returns {node} - GlobalFilterContextProvider
 */
export const GlobalFilterContextProvider = ({
  waterfallFilterStorage,
  supportedWaterfallFilterTypes,
  supportedUrlFilterTypes,
  filterDefaultValue,
  children,
}) => {
  const initialFilters = useMemo(() => {
    const initialWaterfallFilterState = waterfallFilterStorage
      // getFilters must be a synchronized function
      ? waterfallFilterStorage.getFilters(supportedWaterfallFilterTypes)
      : {};

    return _.reduce([
      ...supportedWaterfallFilterTypes,
      ...supportedUrlFilterTypes,
    ], (memo, filterName) => ({
      [filterName]: filterDefaultValue,
      ...memo,
    }), initialWaterfallFilterState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, setFilters] = useState(initialFilters);

  /**
   * Save filter in the state. If shouldUpdateWaterfallFilterStorageis true, will also
   * save it in server.
   * @param {filterObject} filterObject - The filterObject.
   * @param {boolean} shouldUpdateWaterfallFilterStorage - Indicate whether the filter
   * should be saved the filterObject in server.
   */
  const saveFilter = useCallback(({
    filterObj: { filterType, filter },
    shouldUpdateWaterfallFilterStorage,
  }) => {
    if (shouldUpdateWaterfallFilterStorage && waterfallFilterStorage) {
      waterfallFilterStorage.save(filterType, filter);
    }

    setFilters({
      ...filters,
      [filterType]: filter,
    });
  }, [waterfallFilterStorage, filters]);

  /**
   * Update waterfall filter. It will save the filterObject in the server and state.
   * @param {filterObject} filterObject
   */
  const updateWaterfallFilter = useCallback(filterObj => saveFilter({
    filterObj,
    shouldUpdateWaterfallFilterStorage: true,
  }), [saveFilter]);

  /**
   * Update url filter. It will save the filterObject in the state.
   * @param {filterObject} filterObject
   */
  const updateUrlFilter = useCallback(filterObj => saveFilter({
    filterObj,
    shouldUpdateWaterfallFilterStorage: false,
  }), [saveFilter]);

  /**
   * Update filter. It will save the filterObject in the state, and if the filterType is
   * in the supportedWaterfallFilterTypes, it will save the filterObject in the server.
   * @param {filterObject} filterObject
   */
  const updateFilter = useCallback(filterObj => saveFilter({
    filterObj,
    shouldUpdateWaterfallFilterStorage: _.contains(
      supportedWaterfallFilterTypes,
      filterObj.filterType
    ),
  }), [saveFilter, supportedWaterfallFilterTypes]);

  return (
    <GlobalFilterContext.Provider value={{
      updateWaterfallFilter,
      updateUrlFilter,
      updateFilter,
      ...filters,
    }}
    >
      {children}
    </GlobalFilterContext.Provider>
  );
};

GlobalFilterContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  supportedWaterfallFilterTypes: PropTypes.arrayOf(PropTypes.string),
  supportedUrlFilterTypes: PropTypes.arrayOf(PropTypes.string),
  filterDefaultValue: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  waterfallFilterStorage: PropTypes.shape({
    save: PropTypes.func,
    getFilters: PropTypes.func,
  }),
};

GlobalFilterContextProvider.defaultProps = {
  filterDefaultValue: null,
  waterfallFilterStorage: null,
  supportedWaterfallFilterTypes: [],
  supportedUrlFilterTypes: [],
};
